import type { MutableRefObject } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import EditIcon from '@mui/icons-material/Edit';

import type { CheckoutStep, CheckoutStepStatus } from '@t/checkout.types';
import type {
  CustomerStepProps,
  InstallationStepRenderProps,
  PhoneStepRenderProps,
  ReviewStepRenderProps,
} from '@components/Checkout/utils/stepDescriptors';
import { STEP_CONTENT } from '@components/Checkout/utils/stepDescriptors/stepContent';

import type { CheckoutStepDescriptor } from '@components/Checkout/hooks/useCheckoutSteps';

interface CheckoutStepRendererProps {
  steps: CheckoutStepDescriptor[];
  stepStatuses: Record<CheckoutStep, CheckoutStepStatus>;
  activeStep: CheckoutStep;
  stepRefs: MutableRefObject<Record<string, HTMLDivElement | null>>;
  onEditStep: (step: CheckoutStep) => void;
  customerStepProps: CustomerStepProps;
  installationStepProps: InstallationStepRenderProps;
  phoneStepProps: PhoneStepRenderProps;
  reviewStepProps: ReviewStepRenderProps;
}

export const CheckoutStepRenderer = ({
  steps,
  stepStatuses,
  activeStep,
  stepRefs,
  onEditStep,
  customerStepProps,
  installationStepProps,
  phoneStepProps,
  reviewStepProps,
}: CheckoutStepRendererProps) => {
  const totalSteps = steps.length;

  return (
    <>
      {steps.map((step) => {
        const status = stepStatuses[step.key];
        const isActive = activeStep === step.key;
        const isCompleted = status === 'completed';
        const isEditable = isCompleted && !isActive;
        const shouldRenderContent = isActive || isCompleted;

        const handleHeaderClick = isEditable ? () => onEditStep(step.key) : undefined;
        const StepBody = STEP_CONTENT[step.key];

        return (
          <Paper
            variant="checkoutStepCard"
            key={step.key}
            ref={(el: HTMLDivElement | null) => {
              stepRefs.current[step.key] = el;
            }}
          >
            <Paper variant="checkoutStepHeader" onClick={handleHeaderClick}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <Typography className="CheckoutStepLabel">
                  Step {step.number} of {totalSteps}
                </Typography>
                <Typography className="CheckoutStepTitle">{step.label}</Typography>
              </Box>
              {isEditable && (
                <Button
                  variant="contained"
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    onEditStep(step.key);
                  }}
                  startIcon={<EditIcon sx={{ fontSize: '16px' }} />}
                >
                  Edit
                </Button>
              )}
            </Paper>

            {shouldRenderContent && (
              <Paper variant="checkoutStepContent">
                <StepBody
                  isActive={isActive}
                  isCompleted={isCompleted}
                  customerStepProps={customerStepProps}
                  installationStepProps={installationStepProps}
                  phoneStepProps={phoneStepProps}
                  reviewStepProps={reviewStepProps}
                />
              </Paper>
            )}
          </Paper>
        );
      })}
    </>
  );
};
